'use client'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { pct } from '@/lib/format'
import type { SandboxSource, SandboxPrompt } from '../data/types'

interface SourceDetailPanelProps {
  source: SandboxSource | null
  prompts: SandboxPrompt[]
  open: boolean
  onClose: () => void
}

function SourceDetailPanel({ source, prompts, open, onClose }: SourceDetailPanelProps) {
  if (!source) return null

  const citedPrompts = source.promptsCitingIds
    .map((id) => prompts.find((p) => p.id === id))
    .filter((p): p is SandboxPrompt => Boolean(p))

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-72 sm:w-80 overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-base break-all">{source.domain}</SheetTitle>
          <p className="text-xs text-muted-foreground">
            {source.citationCount} citations · {pct(source.sharePercent / 100)} share
          </p>
        </SheetHeader>

        <div>
          <p className="mb-2 u-label">Cited in prompts ({citedPrompts.length})</p>
          <ul className="space-y-1.5">
            {citedPrompts.map((p) => (
              <li key={p.id} className="rounded-md bg-muted px-2 py-1.5 text-xs text-foreground">
                {p.text}
              </li>
            ))}
          </ul>
        </div>
      </SheetContent>
    </Sheet>
  )
}

interface SourcesPageProps {
  sources: SandboxSource[]
  prompts: SandboxPrompt[]
  expandedRowId: string | null
  onToggleRow: (id: string) => void
}

export function SourcesPage({ sources, prompts, expandedRowId, onToggleRow }: SourcesPageProps) {
  const sorted = [...sources].sort((a, b) => b.citationCount - a.citationCount)
  const selectedSource = sorted.find((s) => s.domain === expandedRowId) ?? null
  const maxCount = sorted[0]?.citationCount ?? 1

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[380px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1.5 pl-3 pr-2 text-left u-label w-8">#</th>
            <th className="py-1.5 px-2 text-left u-label">Domain</th>
            <th className="py-1.5 px-2 text-left u-label">Citations</th>
            <th className="py-1.5 pl-2 pr-3 text-left u-label">Share</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((source, index) => (
            <tr
              key={source.domain}
              className={cn(
                'cursor-pointer border-b border-border transition-colors duration-150 hover:bg-muted/50',
                expandedRowId === source.domain && 'bg-muted/30'
              )}
              onClick={() => onToggleRow(expandedRowId === source.domain ? '' : source.domain)}
            >
              <td className="py-2 pl-3 pr-2 text-xs text-muted-foreground tabular-nums">
                {index + 1}
              </td>
              <td className="py-2 px-2 text-xs font-medium text-foreground break-all">
                <button
                  type="button"
                  className="focus-ring rounded-sm text-left"
                  aria-haspopup="dialog"
                >
                  {source.domain}
                </button>
              </td>
              <td className="py-2 px-2 text-xs tabular-nums font-mono">{source.citationCount}</td>
              <td className="py-2 pl-2 pr-3">
                <div className="flex items-center gap-2">
                  <div className="relative h-1.5 w-14 overflow-hidden rounded-full bg-muted">
                    <div
                      className="absolute left-0 top-0 h-full rounded-full bg-foreground"
                      style={{ width: `${(source.citationCount / maxCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs tabular-nums font-mono">
                    {pct(source.sharePercent / 100)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SourceDetailPanel
        source={selectedSource}
        prompts={prompts}
        open={Boolean(expandedRowId)}
        onClose={() => onToggleRow('')}
      />
    </div>
  )
}
